import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { usePreviewAwareAuth } from "@/contexts/SuperAdminPreviewContext";

const normalizeLabel = (label?: string | null) =>
  (label ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

export function isHqSupportLabel(label?: string | null) {
  const normalized = normalizeLabel(label);
  return normalized === "accueil" || normalized === "securite";
}

export function useIsHqSupportProfile() {
  const { userProfile } = usePreviewAwareAuth();
  const storeId = userProfile?.store_id ?? null;
  const hasSupportLabel = isHqSupportLabel(userProfile?.job_function_label);

  const { data: isHqStore = false, isLoading } = useQuery({
    queryKey: ["hq-support-store", storeId],
    enabled: !!storeId && hasSupportLabel,
    staleTime: 1000 * 60 * 30,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("stores")
        .select("is_centrale_hq")
        .eq("id", storeId!)
        .maybeSingle();
      if (error || !data) return false;
      return !!(data as { is_centrale_hq: boolean | null }).is_centrale_hq;
    },
  });

  const isHqSupport = useMemo(
    () => hasSupportLabel && !!storeId && !!isHqStore,
    [hasSupportLabel, storeId, isHqStore]
  );

  return {
    isHqSupport,
    isLoading: hasSupportLabel && !!storeId ? isLoading : false,
  };
}